import React from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
} from "reactstrap";
import QrCode from "qrcode.react";

import { dateFormatter } from "../../util/formatter";
import LoadingOverlay from "./LoadingOverlay";
import LoadingButtonIcon from "@/views/Components/LoadingButtonIcon";
import PropTypes from "prop-types";

const titles = {
  amount: "Amount",
  currencyName: "Currency Name",
  currencySymbol: "Currency Symbol",
  description: "Description",
  destinationAccountId: "Destination Account ID",
  destinationBankName: "Destination Bank Name",
  destinationBankParticipantCode: "Destination Bank Participant Code",
  destinationName: "Destination Full name",
  destinationPhoneNumber: "Destination Phone number",
  errorMessage: "Error Message",
  fastTransactionId: "Fast Transaction ID",
  fiTransactionId: "FI Transaction ID",
  precision: "Precision",
  sourceAccountId: "Source Account ID",
  sourceBankName: "Source Bank Name",
  sourceBankParticipantCode: "Source Bank Participant Code",
  sourceName: "Source Full name",
  sourcePhoneNumber: "Source Phone number",
  status: "Status",
  transactionCreatedTime: "Creation Time",
  transactionHash: "Hash of Transaction",
  transactionWalletType: "Transaction Wallet Type",
  sourceWalletType: "Source Wallet Type",
  destinationWalletType: "Destination Wallet Type",
  assetId: "Asset ID",
  receiverBankAccount: "Receiver Bank Account",
  transactionType: "Transaction Type",
  fstTrxStatus: "Fast Transaction Status",
  qrCode: "QR Code",
};

const TransactionDetails = ({
  loading,
  details,
  onBack,
  type,
  isRefunding,
  isAcknowledging,
  onClickRefund,
  onClickAcknowledge,
}) => {
  const {
    destinationWalletType,
    transactionType,
    fstTrxStatus,
    sourceWalletType,
  } = details;
  const beautifulDetails = Object.keys(details)
    .map(x => ({
      title: titles[x] || x,
      key: x,
      value:
        x === "transactionCreatedTime" ? dateFormatter(details[x]) : details[x],
    }))
    .filter(
      detail =>
        !(
          detail.title === titles.destinationName &&
          destinationWalletType !== "MOBILE"
        ) &&
        !(detail.title === titles.sourceName && sourceWalletType !== "MOBILE"),
    );

  const shouldRenderDepositActionsButtons =
    type === "incoming" &&
    destinationWalletType === "BANK" &&
    transactionType === "Deposit" &&
    fstTrxStatus === "RECEIVED_AT_FI";

  const depositActionsButtons = (
    <>
      <Button
        type="submit"
        color="primary"
        onClick={onClickRefund}
        disabled={isAcknowledging}
        style={{ marginLeft: 20, width: 200 }}>
        <i className="fa fa-reply mr-2" />
        <span>Refund</span>
        <LoadingButtonIcon loading={isRefunding} />
      </Button>
      <Button
        type="submit"
        color="success"
        onClick={onClickAcknowledge}
        disabled={isRefunding}
        style={{ marginLeft: 20, width: 200 }}>
        <i className="fa fa-check-circle mr-2" />
        <span>Acknowledge</span>
        <LoadingButtonIcon loading={isAcknowledging} />
      </Button>
    </>
  );

  return (
    <div>
      <Row>
        <Col>
          <Card style={{ position: "relative" }}>
            <LoadingOverlay loading={loading} />
            <CardHeader>Transactions Detail</CardHeader>
            <CardBody>
              <Row>
                <Col
                  sm={{
                    size: 12,
                  }}
                  lg={{
                    size: 11,
                  }}
                  xl={{
                    size: 9,
                  }}>
                  <Table hover bordered striped responsive>
                    <tbody>
                      {beautifulDetails.length ? (
                        beautifulDetails
                          .filter(detail => detail.value)
                          .map(detail => {
                            if (detail.key === "qrCode") {
                              return (
                                <tr key={detail.key}>
                                  <td>{detail.title}</td>
                                  <td>
                                    <QrCode
                                      id="qrCode"
                                      size={256}
                                      value={detail.value}
                                    />
                                  </td>
                                </tr>
                              );
                            }

                            if (detail.key === "transactionContent") {
                              return (
                                detail.value.details.sourceOfFunds && (
                                  <>
                                    <tr key={`${detail.key}Name`}>
                                      <td>Transfer from (name)</td>
                                      <td>
                                        {
                                          detail.value.details.sourceOfFunds
                                            .name
                                        }
                                      </td>
                                    </tr>
                                    <tr key={`${detail.key}Phone`}>
                                      <td>Transfer from (phone)</td>
                                      <td>
                                        {
                                          detail.value.details.sourceOfFunds
                                            .phoneNumber
                                        }
                                      </td>
                                    </tr>
                                  </>
                                )
                              );
                            }

                            return (
                              <tr key={detail.key}>
                                <td>{detail.title}</td>
                                <td>{detail.value}</td>
                              </tr>
                            );
                          })
                      ) : (
                        <tr>
                          <td>
                            {loading ? "Loading..." : "No transaction info"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Button
                    style={{ width: 100, marginLeft: 15 }}
                    color="primary"
                    type="button"
                    onClick={onBack}
                    data-cy="back">
                    <i className="fa fa-arrow-left mr-2" />
                    Back
                  </Button>
                  {shouldRenderDepositActionsButtons && depositActionsButtons}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

TransactionDetails.propTypes = {
  loading: PropTypes.bool.isRequired,
  details: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["incoming", "outgoing"]).isRequired,
  isRefunding: PropTypes.bool.isRequired,
  isAcknowledging: PropTypes.bool.isRequired,
  onClickRefund: PropTypes.func.isRequired,
  onClickAcknowledge: PropTypes.func.isRequired,
};

export default TransactionDetails;
